import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { ToastManager } from '@features/ToastManager';
import { UiButton, UiText } from '@shared/ui';

import cls from './ProfileEditForm.module.css';
import { PROFILE_TOAST_SCOPE } from '../../model/toast';
import { AvatarTabContent } from '../AvatarTabContent';
import { BonusTabContent } from '../BonusTabContent';
import { MainTabContent } from '../MainTabContent';
import { ProfileCard } from '../ProfileCard';
import { SettingsTabContent } from '../SettingsTabContent';

import type { Profile } from '@entities/profile';

type ProfileTab = 'main' | 'avatar' | 'settings' | 'bonus';

type ProfileEditFormProps = {
    profile: Profile;
    onPaySubscriptionClick?: ({ removeProlong }: { removeProlong: boolean }) => void;
};

export const ProfileEditForm = ({ profile, onPaySubscriptionClick }: ProfileEditFormProps) => {
    const { t } = useTranslation();
    const tabPanelRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<ProfileTab>('main');
    const [tabPanelMaxHeight, setTabPanelMaxHeight] = useState<number>();

    useLayoutEffect(() => {
        const updateTabPanelMaxHeight = () => {
            const tabPanel = tabPanelRef.current;

            if (!tabPanel) {
                return;
            }

            setTabPanelMaxHeight(
                Math.max(window.innerHeight - tabPanel.getBoundingClientRect().top - 50, 0)
            );
        };

        updateTabPanelMaxHeight();

        window.addEventListener('resize', updateTabPanelMaxHeight);
        window.addEventListener('scroll', updateTabPanelMaxHeight, true);

        return () => {
            window.removeEventListener('resize', updateTabPanelMaxHeight);
            window.removeEventListener('scroll', updateTabPanelMaxHeight, true);
        };
    }, [activeTab]);

    const tabs: Array<{ title: string; value: ProfileTab }> = [
        { title: t('profile.page.tabs.main'), value: 'main' },
        { title: t('profile.page.tabs.avatar'), value: 'avatar' },
        { title: t('profile.page.tabs.settings'), value: 'settings' },
        { title: t('profile.page.tabs.bonus'), value: 'bonus' },
    ];

    const tabContent: Record<ProfileTab, ReactNode> = {
        main: <MainTabContent profile={profile} />,
        avatar: <AvatarTabContent />,
        settings: (
            <SettingsTabContent profile={profile} onPaySubscriptionClick={onPaySubscriptionClick} />
        ),
        bonus: <BonusTabContent profile={profile} />,
    };

    return (
        <ProfileCard className={cls.profileEditForm}>
            <div className={cls.editHeader}>
                <UiText tag="h2" variant="subHeading" weight="bold">
                    {t('profile.page.edit.title')}
                </UiText>
                <div className={cls.tabs} role="tablist">
                    {tabs.map(({ title, value }) => (
                        <UiButton
                            key={value}
                            id={`profile-tab-${value}`}
                            type="button"
                            variant="text"
                            className={cn(cls.tab, activeTab === value && cls.activeTab)}
                            btnText={title}
                            role="tab"
                            aria-selected={activeTab === value}
                            aria-controls="profile-tab-panel"
                            onClick={() => setActiveTab(value)}
                        />
                    ))}
                </div>
            </div>
            <div
                ref={tabPanelRef}
                id="profile-tab-panel"
                className={cls.tabPanel}
                role="tabpanel"
                aria-labelledby={`profile-tab-${activeTab}`}
                style={
                    tabPanelMaxHeight === undefined ? undefined : { maxHeight: tabPanelMaxHeight }
                }
            >
                {tabContent[activeTab]}
            </div>
            <ToastManager toastPosition="right" scope={PROFILE_TOAST_SCOPE} />
        </ProfileCard>
    );
};
